import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { OrderCandidate } from "@/core";
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  ExternalLink,
  Link2,
  ShieldAlert,
} from "lucide-react";
import { Link } from "react-router-dom";
import { DownstreamEffectsList } from "./DownstreamEffectsList";
import { WorkflowSummaryCard } from "./WorkflowSummaryCard";
import {
  formatCurrency,
  formatNumber,
  getSuggestionAttentionDetails,
} from "./operations-shared";

interface CreatedRecordLink {
  label: string;
  href: string;
}

interface SuggestionApprovalPreviewProps {
  candidate: OrderCandidate;
  workspaceHref?: string;
  recordHref?: string;
  inventorySufficiency?: {
    allocationPossible: boolean;
    availableMiles: number;
    shortfallMiles: number;
    inventoryCount: number;
    maxSingleInventoryMiles: number;
  };
  createdRecordLinks: CreatedRecordLink[];
  crmHref?: string;
  onNavigate?: () => void;
}

function getSnapshotRecord(payload: {
  candidate: OrderCandidate;
}): Record<string, unknown> {
  return (payload.candidate.snapshot || {}) as Record<string, unknown>;
}

function getOptionalString(payload: {
  record: Record<string, unknown>;
  key: string;
}): string | undefined {
  const value = payload.record[payload.key];
  return typeof value === "string" && value.trim().length > 0
    ? value
    : undefined;
}

export function SuggestionApprovalPreview(
  props: Readonly<SuggestionApprovalPreviewProps>,
) {
  const {
    candidate,
    workspaceHref,
    recordHref,
    inventorySufficiency,
    createdRecordLinks,
    crmHref,
    onNavigate,
  } = props;

  const snapshot = getSnapshotRecord({ candidate });
  const accountEmail = getOptionalString({
    record: snapshot,
    key: "accountEmail",
  });
  const accountPassword = getOptionalString({
    record: snapshot,
    key: "accountPassword",
  });
  const paymentOption = getOptionalString({
    record: snapshot,
    key: "paymentOption",
  });
  const hasCompleteAccount = Boolean(accountEmail && accountPassword);
  const attention = getSuggestionAttentionDetails({
    status: candidate.status,
    candidateType: candidate.candidateType,
    hasCompleteAccount,
    allocationPossible: inventorySufficiency?.allocationPossible,
  });

  const warnings = [
    candidate.status === "invalidated"
      ? {
          icon: ShieldAlert,
          title: "Suggestion invalidated",
          description:
            "The source deal no longer maps cleanly to an approvable suggestion. Review the source deal before taking action.",
          variant: "destructive" as const,
        }
      : null,
    candidate.status === "converted"
      ? {
          icon: CheckCircle2,
          title: "Suggestion already converted",
          description:
            "This suggestion has already been turned into downstream records. Review the created records before taking any further action.",
          variant: "default" as const,
        }
      : null,
    candidate.candidateType === "po" && !hasCompleteAccount
      ? {
          icon: AlertTriangle,
          title: "Missing account credentials",
          description:
            "Approval will create the inventory in a needs-reset state because the snapshot does not contain complete credentials.",
          variant: "default" as const,
        }
      : null,
    candidate.candidateType === "so" &&
    inventorySufficiency &&
    !inventorySufficiency.allocationPossible
      ? {
          icon: AlertTriangle,
          title: "Insufficient inventory",
          description:
            "No single eligible inventory can fulfill this suggestion right now. Approval will fail until inventory is replenished or rebalanced.",
          variant: "destructive" as const,
        }
      : null,
  ].filter(
    (
      warning,
    ): warning is {
      icon: typeof AlertTriangle;
      title: string;
      description: string;
      variant: "default" | "destructive";
    } => Boolean(warning),
  );

  return (
    <div className="space-y-4">
      <WorkflowSummaryCard
        title="Review Summary"
        description="Validate the candidate details, downstream records, and system behavior before approval."
        status={candidate.status}
        health={attention.state}
        healthReason={attention.reason}
        metrics={[
          { label: "Type", value: candidate.candidateType.toUpperCase() },
          {
            label: candidate.candidateType === "so" ? "Miles" : "Quantity",
            value: formatNumber({ value: candidate.quantity }),
          },
          {
            label: "Amount",
            value: formatCurrency({ value: candidate.amount }),
          },
          {
            label: "Customer",
            value:
              candidate.customerDisplayName ||
              candidate.customerEmail ||
              "Unknown customer",
          },
        ]}
        footer={
          <div className="flex flex-wrap gap-2">
            {workspaceHref ? (
              <Button asChild size="sm" variant="outline">
                <Link to={workspaceHref} onClick={onNavigate}>
                  Open Workspace
                  <ArrowRightLeft className="h-4 w-4" />
                </Link>
              </Button>
            ) : recordHref ? (
              <Button asChild size="sm" variant="outline">
                <Link to={recordHref} onClick={onNavigate}>
                  {candidate.candidateType === "po"
                    ? "Open Purchase Order"
                    : "Open Sales Order"}
                  <ArrowRightLeft className="h-4 w-4" />
                </Link>
              </Button>
            ) : null}
            {candidate.sourceDealId ? (
              <Button asChild size="sm" variant="outline">
                <Link
                  to={`/deals?dealId=${candidate.sourceDealId}`}
                  onClick={onNavigate}
                >
                  View Source Deal
                  <Link2 className="h-4 w-4" />
                </Link>
              </Button>
            ) : null}
            {crmHref ? (
              <Button asChild size="sm" variant="outline">
                <Link to={crmHref} target="_blank" rel="noreferrer">
                  Open in CRM
                  <ExternalLink className="h-4 w-4" />
                </Link>
              </Button>
            ) : null}
          </div>
        }
      />

      {warnings.map((warning) => {
        const Icon = warning.icon;

        return (
          <Alert key={warning.title} variant={warning.variant}>
            <Icon className="h-4 w-4" />
            <AlertTitle>{warning.title}</AlertTitle>
            <AlertDescription>{warning.description}</AlertDescription>
          </Alert>
        );
      })}

      <Card className="border-border/70">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Review Snapshot</CardTitle>
          <CardDescription>
            Operational inputs the system will use during approval.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border bg-muted/20 p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Product
            </div>
            <div className="mt-1 text-sm font-semibold">
              {candidate.productName}
            </div>
          </div>
          <div className="rounded-lg border bg-muted/20 p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Unit Price
            </div>
            <div className="mt-1 text-sm font-semibold">
              {formatCurrency({ value: candidate.unitPrice })}
            </div>
          </div>
          {candidate.candidateType === "po" ? (
            <>
              <div className="rounded-lg border bg-muted/20 p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Payment Timing
                </div>
                <div className="mt-1 text-sm font-semibold">
                  {paymentOption || "pay_in_advance"}
                </div>
              </div>
              <div className="rounded-lg border bg-muted/20 p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Account Snapshot
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm font-semibold">
                  <Badge variant={hasCompleteAccount ? "secondary" : "outline"}>
                    {hasCompleteAccount ? "Complete" : "Placeholder account"}
                  </Badge>
                  <span className="text-muted-foreground">
                    {accountEmail || "No email captured"}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-lg border bg-muted/20 p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Revenue Snapshot
                </div>
                <div className="mt-1 text-sm font-semibold">
                  {formatCurrency({ value: candidate.amount })}
                </div>
              </div>
              <div className="rounded-lg border bg-muted/20 p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Inventory Sufficiency
                </div>
                <div className="mt-1 text-sm font-semibold">
                  {inventorySufficiency
                    ? inventorySufficiency.allocationPossible
                      ? "Sufficient"
                      : "Insufficient"
                    : "Pending inventory check"}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <DownstreamEffectsList
        title="What Happens On Approval"
        description="Approval creates or updates these downstream operational records."
        effects={
          candidate.candidateType === "po"
            ? [
                {
                  label: "Purchase Order",
                  description:
                    "Creates a purchase order linked back to this suggestion and source deal.",
                },
                {
                  label: "Inventory",
                  description:
                    "Creates an inventory aggregate that becomes the main operational workspace.",
                },
                {
                  label: "Inventory Credentials",
                  description: hasCompleteAccount
                    ? "Stores the captured credentials directly on the new inventory."
                    : "Creates the inventory in a needs-reset state until credentials are completed.",
                },
              ]
            : [
                {
                  label: "Sales Order",
                  description:
                    "Creates a sales order linked directly to an inventory aggregate.",
                },
                {
                  label: "Inventory Resolution",
                  description:
                    "Resolves the inventory that will fulfill the order.",
                },
                {
                  label: "Inventory Rollups",
                  description:
                    "Updates inventory availability inside the shared workspace.",
                },
              ]
        }
      />

      {candidate.candidateType === "so" && inventorySufficiency ? (
        <Card className="border-border/70">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Inventory Resolution</CardTitle>
            <CardDescription>
              Approval will auto-resolve the oldest eligible inventory that can
              fully satisfy this sell order.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Eligible Inventories
              </div>
              <div className="mt-1 text-sm font-semibold">
                {formatNumber({ value: inventorySufficiency.inventoryCount })}
              </div>
            </div>
            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Total Eligible Miles
              </div>
              <div className="mt-1 text-sm font-semibold">
                {formatNumber({ value: inventorySufficiency.availableMiles })}
              </div>
            </div>
            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Largest Single Inventory
              </div>
              <div className="mt-1 text-sm font-semibold">
                {formatNumber({
                  value: inventorySufficiency.maxSingleInventoryMiles,
                })}
              </div>
            </div>
            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Single-Inventory Shortfall
              </div>
              <div className="mt-1 text-sm font-semibold">
                {formatNumber({ value: inventorySufficiency.shortfallMiles })}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {candidate.status === "converted" ? (
        <Card className="border-border/70">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Created Records</CardTitle>
            <CardDescription>
              These records were already created from this suggestion.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {createdRecordLinks.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                Created records are unavailable.
              </div>
            ) : (
              createdRecordLinks.map((record) => (
                <Button key={record.label} asChild size="sm" variant="outline">
                  <Link to={record.href} onClick={onNavigate}>
                    {record.label}
                  </Link>
                </Button>
              ))
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
